import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
    UntypedFormControl,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import {
    AccountProvisionTriggerSubSource,
    CompanyAccountProvisioningProductType,
    RoleType
} from '../../constants-types';
import { IconSize, IconType, Person, UserUpdate } from '../../core-data-model';
import { notification, PizzaPlaygroundApi } from '../../core-services';
import { EmailValidatorDirective } from '../../core-ui';
import { TranslationService } from '../../translations';

@Component({
    selector: 'create-selfserve-person-form',
    template: `
        <div
            class="createPerson_container border-color-neutral-400 bg-color-neutral-50"
        >
            <div class="layoutLastPushedRight-X">
                <div class="layoutVertical">
                    <span class="largeLabelText" style="margin: 5px 0px 5px">{{
                        TR.key('label.edit-team.add')
                    }}</span>
                    <div
                        class="smallText italicText color-neutral-700"
                        style="margin: 10px 0"
                    >
                        {{ TR.key('teams.sendBA-description') }}
                    </div>
                </div>
            </div>

            <div>
                <div [formGroup]="personCreationForm">
                    <div class="layoutLastPushedRight-X">
                        <div class="form-group" style="margin-right:15px">
                            <label class="control-label">
                                <span class="required">{{
                                    TR.key('label.common-terms.first.name')
                                }}</span>
                                <input
                                    class="form-control input-sm"
                                    type="text"
                                    formControlName="firstName"
                                    maxlength="50"
                                    minlength="2"
                                />
                            </label>
                        </div>
                        <div class="form-group">
                            <label class="control-label">
                                <span class="required">{{
                                    TR.key('label.common-terms.last.name')
                                }}</span>
                                <input
                                    class="form-control input-sm"
                                    type="text"
                                    formControlName="lastName"
                                    maxlength="50"
                                    minlength="2"
                                />
                            </label>
                        </div>
                    </div>
                    <div class="form-group">
                        <label class="control-label" style="max-width:none">
                            <span class="required">{{
                                TR.key('label.common-terms.email')
                            }}</span>
                            <input
                                class="form-control input-sm"
                                type="text"
                                formControlName="email"
                                maxlength="510"
                            />
                        </label>
                    </div>
                    <div class="layoutHorizontal">
                        <button
                            class="button button-sm button-primary"
                            [disabled]="this.personCreationForm.invalid"
                            [ngClass]="{
                                'button--loading ': isCreatingPerson
                            }"
                            (click)="createSelfServeTeamMember()"
                        >
                            {{ TR.key('label.common-terms.invite') }}
                        </button>
                        @if (showCloseButton) {
                            <button
                                class="button button-sm button-secondary"
                                style="margin-left: 10px; flex-grow:0"
                                [disabled]="isCreatingPerson"
                                (click)="close.emit()"
                            >
                                {{ TR.key('text.common-terms.cancel') }}
                            </button>
                        }
                    </div>
                </div>
            </div>
        </div>
    `,
    styles: [
        `
            .createPerson_container {
                padding: 20px 30px 30px 30px;
                border-width: 2px;
                border-style: solid;
                border-radius: 5px;
            }
        `
    ],
    standalone: false
})
export class CreateSelfServeTeamMemberComponent {
    @Output()
    createdPerson = new EventEmitter<Person>();
    @Output()
    close = new EventEmitter();
    @Input()
    showCloseButton: boolean;
    @Input()
    teamId: string;

    IconType = IconType;
    IconSize = IconSize;

    personCreationForm: UntypedFormGroup;
    isCreatingPerson = false;

    constructor(
        public TR: TranslationService,
        private pizzaPlaygroundApi: PizzaPlaygroundApi
    ) {
        this.personCreationForm = new UntypedFormGroup({
            firstName: new UntypedFormControl('', Validators.required),
            lastName: new UntypedFormControl('', Validators.required),
            email: new UntypedFormControl('', [
                Validators.required,
                EmailValidatorDirective.emailValidation
            ])
        });
    }

    @notification(
        'onboarding.emailsuccess-title',
        'onboarding.emailsuccess-message'
    )
    async createSelfServeTeamMember() {
        try {
            this.isCreatingPerson = true;
            const partialUserUpdate = {
                email: this.personCreationForm.get('email').value,
                firstName: this.personCreationForm.get('firstName').value,
                lastName: this.personCreationForm.get('lastName').value,
                role: RoleType.AccountAdmin,
                teamId: this.teamId,
                accountProvisionTriggerSubsource:
                    AccountProvisionTriggerSubSource.DesignSelfServeTeamMember,
                product: CompanyAccountProvisioningProductType.Design
            };

            const teamMemberToCreate = new UserUpdate(partialUserUpdate, null);

            const response =
                await this.pizzaPlaygroundApi.createSelfServePerson(
                    teamMemberToCreate
                );
            this.createdPerson.emit(response);
            this.personCreationForm.reset();
        } finally {
            this.isCreatingPerson = false;
            this.close.emit();
        }
    }
}
